// @givmo/org-console — the reusable DIRECT-LINK verdict gate (OM-12 mechanic 3).
//
// ══ THE GRID IS NOT THE ONLY WAY IN ════════════════════════════════════════════════════════════
//
// The four tile states answer "how does this module's SQUARE render". Once a module is a path inside
// the console, a person can arrive at it without ever seeing a square: a colleague's link, a
// bookmark, a mail, the address bar. That arrival needs the SAME answer, rendered as a page instead
// of a tile — and it needs it from the console's ONE standing read, never from a query of its own.
//
// So this gate wraps a module's route tree and turns the read into one of six answers. Five of them
// render something honest; the sixth renders the module. Since shell contract v3 the two no-access
// answers (`locked`, and `off` for someone who cannot flip the switch) carry the SAME request door
// and request sub-states the tile does — a deep link and a tile must agree.
//
// ══ THE INPUT IS THREE SIGNALS, NOT ONE ════════════════════════════════════════════════════════
//
// `standings === null` alone cannot carry this, because it conflates three different facts: a read in
// flight, a read that FAILED, and a read that SUCCEEDED against a backend predating the fields. The
// grid can conflate them — its answer for all three is the same permissive one — but a page cannot:
// the first must not flash module content, the second must not be reported as a refusal, and the
// third must render the module. Hence `{ pending, errored, standings }`.
//
// ══ IT RENDERS IN PLACE, AND NEVER REDIRECTS ═══════════════════════════════════════════════════
//
// Deliberately unlike the tier guard on the organization's own pages, which sends a resolved
// non-admin to their grid. A person who followed a link to a module is owed an answer AT the place
// they arrived; bouncing them to a grid makes the link look broken and tells them nothing about why.
// The one exception is not a redirect either: a module this build does not serve, or one this
// organization is not offered, is genuinely NOT THERE, and the honest surface for that is the host's
// own not-found page — the same page a typed-wrong URL reaches.
//
// ══ WHAT THIS GATE IS NOT ══════════════════════════════════════════════════════════════════════
//
// It answers "may this person open this module at all". Everything INSIDE — which pages, which
// records, which controls — stays the module's own business, checked by the module against its own
// backend. A gate that tried to answer both would put one module's authorization model into shared
// machinery, which is the fork this package exists to prevent.

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  MODULE_SQUARE_DECLINED_LINE,
  MODULE_SQUARE_DECLINED_NO_NOTE,
  MODULE_SQUARE_DECLINED_WHY,
  MODULE_SQUARE_NO_ACCESS_LINE,
  MODULE_SQUARE_REQUEST_LINE,
  MODULE_SQUARE_REQUESTED_LINE,
  MODULE_SQUARE_SETTING_UP_LINE,
  MODULE_SQUARE_TURN_ON_LINE,
  shouldInterceptClick,
  testIdProp,
  type ModuleSquareAccessRequest,
} from '@givmo/design-system';
import { Lock } from 'lucide-react';
import { ORG_TIER_UNKNOWN_LINE } from './org-console-notices';
import { verdictFor, type ModuleStandings } from './module-verdict';

/**
 * What the console's ONE standing read is saying right now.
 *
 * ⚠ ALL THREE COME FROM THAT SINGLE READ — never from a query this gate opens. A second observer of
 * the standing key, mounted underneath a shell that itself waits on the first, is a measured
 * unbounded refetch loop; the host already holds these signals and passes them down.
 */
export interface ModuleReadState {
  /** The read has not settled. */
  pending: boolean;
  /** The read settled, unsuccessfully. Recoverable, and the only state offering a retry. */
  errored: boolean;
  /** Per-module standing, or `null` when the read reported none. */
  standings: ModuleStandings | null;
}

/**
 * How a whole module renders to whoever just arrived at it.
 *
 * `setting-up` is a reachable first-class answer. It renders in place, never falls through to module
 * content, and carries no refusal reason — that reason belongs only beside the switch that started
 * the hook.
 */
export type ModuleGateVerdict =
  | 'pending'
  | 'unavailable'
  | 'absent'
  | 'off'
  | 'setting-up'
  | 'locked'
  | 'link';

export interface ModuleVerdictOptions {
  /**
   * Whether this build serves the module at all (`isMountedInThisBuild` in `./module-routes.ts`).
   *
   * `false` short-circuits everything else: an address this build does not serve is not-found in
   * every environment where it is not configured, and waiting on a standing read to say so would
   * hold a person on a spinner to answer a question the build already knows.
   */
  mounted?: boolean;
}

/**
 * The rule, as a pure function — seven answers over three signals, in precedence order.
 *
 * | input                                        | answer        |
 * | -------------------------------------------- | ------------- |
 * | not mounted in this build                    | `absent`      |
 * | pending                                      | `pending`     |
 * | settled, errored                             | `unavailable` |
 * | settled, nothing reported                    | `link`        |
 * | reported, this module missing from the map   | `absent`      |
 * | reported                                     | the tile rule |
 *
 * ⚠ THE FOURTH ROW IS THE ONE THAT LOOKS WRONG AND IS NOT. A settled, successful read that carries
 * NO standings is the permissive case the grid already answers by rendering every built square as an
 * ordinary link — a backend that predates the fields, most of all. Refusing here instead would take
 * a module away from everyone for the length of a rollout, on the strength of a fact nobody asserted.
 * The module's own reads still answer for themselves behind this.
 */
export function moduleGateVerdict(
  slug: string,
  read: ModuleReadState,
  options: ModuleVerdictOptions = {},
): ModuleGateVerdict {
  if (options.mounted === false) return 'absent';
  if (read.pending) return 'pending';
  if (read.errored) return 'unavailable';
  if (read.standings === null) return 'link';
  return verdictFor(read.standings, slug);
}

/** {@link moduleGateVerdict} for a component. Memoized so it is stable in an effect's deps. */
export function useModuleVerdict(
  slug: string,
  read: ModuleReadState,
  options: ModuleVerdictOptions = {},
): ModuleGateVerdict {
  const { pending, errored, standings } = read;
  const { mounted } = options;
  return useMemo(
    () => moduleGateVerdict(slug, { pending, errored, standings }, { mounted }),
    [slug, pending, errored, standings, mounted],
  );
}

export interface ModuleGateProps extends ModuleVerdictOptions {
  /** The module being opened. */
  slug: string;
  /** The console's one standing read. */
  read: ModuleReadState;
  /** The module itself — rendered only on `link`. */
  children: ReactNode;
  /**
   * The host's not-found surface, for a module that is not there.
   *
   * ⚠ REQUIRED, AND IT IS THE HOST'S. Not-found is a whole-app surface with the host's chrome and
   * its own way back; a notice invented here would be a second, worse one — and would be the one
   * place a person sees a page that does not look like the rest of the app.
   */
  renderNotFound: () => ReactNode;
  /** What renders while the standing read settles. Nothing, by default. */
  renderPending?: () => ReactNode;
  /** Re-run the standing read. Offered with the unavailable notice; omitted, no retry renders. */
  onRetry?: () => void;
  /**
   * Where someone who may turn this module on goes to do it — the organization's own page.
   *
   * `null` or omitted renders the STATEMENT instead, which is the honest shape for a person who
   * cannot flip the switch: naming an act they cannot perform is the decorative control this estate
   * refuses everywhere.
   */
  turnOnHref?: string | null;
  /** Follow {@link turnOnHref} without a document load, when the host runs a router. */
  onNavigate?: (href: string) => void;
  /** The module's name, for the copy. Falls back to the slug. */
  label?: string;
  /**
   * Open the host's request dialog for this module (shell contract v3, T5.1). The `locked` and
   * non-actionable `off` answers render the SAME request affordance the tile carries — a deep link
   * and a tile must agree. ABSENT ⇒ no request door (the honesty law).
   */
  onRequestAccess?: () => void;
  /** The caller's own request fact on this module — the `requested` / `declined` sub-states. */
  accessRequest?: ModuleSquareAccessRequest;
  /** Test-id namespace. Defaults to `module`. */
  testIdPrefix?: string;
}

/** The default test-id namespace. Part of the test-id contract — treat a change like an API change. */
export const MODULE_GATE_TEST_ID_PREFIX = 'module';

export function ModuleGate({
  slug,
  read,
  mounted,
  children,
  renderNotFound,
  renderPending,
  onRetry,
  turnOnHref,
  onNavigate,
  label,
  onRequestAccess,
  accessRequest,
  testIdPrefix = MODULE_GATE_TEST_ID_PREFIX,
}: ModuleGateProps) {
  const verdict = useModuleVerdict(slug, read, { mounted });
  const name = (label ?? '').trim().length > 0 ? (label as string) : slug;
  const testId = (suffix: string) => `${testIdPrefix}-gate-${suffix}`;

  /**
   * The no-access answer's action — the tile's own states as a page: PENDING is a statement,
   * DECLINED shows the note and the way to ask again, otherwise the request door when wired.
   */
  const requestAction = (() => {
    if (accessRequest?.status === 'pending') {
      return (
        <p
          className="font-sans text-s14 font-w500 tracking-givmo text-text-tertiary"
          {...testIdProp(testId('requested'))}
        >
          {MODULE_SQUARE_REQUESTED_LINE}
        </p>
      );
    }
    const door =
      onRequestAccess === undefined ? null : (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          className="max-sm:h-44 max-sm:px-16"
          onClick={onRequestAccess}
          testId={testId('request')}
        >
          {MODULE_SQUARE_REQUEST_LINE}
        </Button>
      );
    if (accessRequest?.status === 'declined') {
      const note = (accessRequest.note ?? '').trim();
      return (
        <div className="flex flex-col items-center gap-8" {...testIdProp(testId('declined'))}>
          <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {MODULE_SQUARE_DECLINED_LINE}
          </p>
          <p className="max-w-[360px] font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            <span className="font-w600 text-text-strong">{MODULE_SQUARE_DECLINED_WHY}: </span>
            {note.length > 0 ? `“${note}”` : MODULE_SQUARE_DECLINED_NO_NOTE}
          </p>
          {door}
        </div>
      );
    }
    return door ?? undefined;
  })();

  switch (verdict) {
    case 'link':
      return <>{children}</>;

    case 'pending':
      // Nothing by default: the shell around this has already painted, and a second spinner inside
      // it is a frame of chrome that exists only to be replaced.
      return <>{renderPending?.() ?? null}</>;

    case 'absent':
      return <>{renderNotFound()}</>;

    case 'unavailable':
      // ⚠ A FAILED READ IS NOT A REFUSAL. Rendering "you may not open this" here would assert
      // something the surface never learned; the notice says what actually happened and offers the
      // one thing that can change it. A host that supplies no retry keeps the same sentence — the
      // state is the same fact whether or not this surface can act on it.
      return (
        <Card>
          <CardBody className="flex flex-col items-start gap-12">
            <p
              className="font-sans text-s14 text-text-secondary"
              {...testIdProp(testId('unavailable'))}
            >
              {ORG_TIER_UNKNOWN_LINE}
            </p>
            {onRetry !== undefined && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={onRetry}
                testId={testId('retry')}
              >
                Try again
              </Button>
            )}
          </CardBody>
        </Card>
      );

    case 'off':
      return (
        <EmptyState
          testId={testId('off')}
          icon={<Icon icon={Lock} size={20} aria-hidden />}
          title={name}
          description={MODULE_SQUARE_NO_ACCESS_LINE}
          action={
            turnOnHref === undefined || turnOnHref === null ? (
              requestAction
            ) : (
              <a
                href={turnOnHref}
                onClick={(event) => {
                  if (onNavigate === undefined || !shouldInterceptClick(event)) return;
                  event.preventDefault();
                  onNavigate(turnOnHref);
                }}
                className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline max-sm:inline-flex max-sm:min-h-44 max-sm:items-center"
                {...testIdProp(testId('turn-on'))}
              >
                {MODULE_SQUARE_TURN_ON_LINE}
              </a>
            )
          }
        />
      );

    case 'locked':
      // The same sentence the tile carries, and the same door: a request for a role in this module
      // when the host wired one, the request's own sub-state otherwise (shell contract v3).
      return (
        <EmptyState
          testId={testId('locked')}
          icon={<Icon icon={Lock} size={20} aria-hidden />}
          title={name}
          description={MODULE_SQUARE_NO_ACCESS_LINE}
          action={requestAction}
        />
      );

    case 'setting-up':
      // The state is org-wide, so it beats the caller's grant exactly as OFF does. A Global Admin may
      // return to the management page; everyone else gets the same honest state as a statement. No
      // activation reason is accepted by this component, which structurally keeps it off the gate.
      return (
        <EmptyState
          testId={testId('setting-up')}
          icon={<Icon icon={Lock} size={20} aria-hidden />}
          title={name}
          description={MODULE_SQUARE_SETTING_UP_LINE}
          action={
            turnOnHref === undefined || turnOnHref === null ? undefined : (
              <a
                href={turnOnHref}
                onClick={(event) => {
                  if (onNavigate === undefined || !shouldInterceptClick(event)) return;
                  event.preventDefault();
                  onNavigate(turnOnHref);
                }}
                className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline max-sm:inline-flex max-sm:min-h-44 max-sm:items-center"
                {...testIdProp(testId('setup'))}
              >
                View setup
              </a>
            )
          }
        />
      );
  }
}
